// src/pages/MyLibrary.jsx
// Dedicated premium "My Library" page — shows progress cards & gamification dashboard.

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import {
  getReadingHistory,
  calculateStreak,
  getDailyGoalProgress,
  getLast30DaysActivity,
  checkAchievements
} from "../utils/gamification";

import StreakCard from "../components/library/StreakCard";
import GoalProgressCard from "../components/library/GoalProgressCard";
import ActivitySoundwave from "../components/library/ActivitySoundwave";
import HardcopyRewardsShelf from "../components/library/HardcopyRewardsShelf";
import ClaimPassModal from "../components/library/ClaimPassModal";
import BadgesGallery from "../components/library/BadgesGallery";
import StudyNotebook from "./StudyNotebook";
import { useAuth } from "../context/AuthContext";

export default function MyLibrary({ books }) {
  const navigate = useNavigate();
  const { user, syncing, syncToCloud } = useAuth();
  const [activeTab, setActiveTab] = useState("reading"); // "reading" | "stats"
  const [showGoalSettings, setShowGoalSettings] = useState(false);
  
  // Custom daily goal state (synced with localStorage)
  const [dailyGoal, setDailyGoal] = useState(() => {
    try {
      const saved = localStorage.getItem("ebenezer_daily_goal");
      return saved ? parseInt(saved, 10) || 10 : 10;
    } catch (e) {
      return 10;
    }
  });

  const handleGoalChange = (e) => {
    const nextVal = parseInt(e.target.value, 10) || 10;
    setDailyGoal(nextVal);
    localStorage.setItem("ebenezer_daily_goal", String(nextVal));
  };

  // Claim state for physical hardcopy rewards
  const [claimedHardcopies, setClaimedHardcopies] = useState(() => {
    try {
      const saved = localStorage.getItem("ebenezer_claimed_hardcopies");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });
  const [activeClaimPass, setActiveClaimPass] = useState(null);

  const claimHardcopy = (rewardId, days, title, subtitle) => {
    const randomHex = Math.floor(Math.random() * 16777215).toString(16).toUpperCase().padStart(6, "0");
    const code = `EB-HC-${days}D-${randomHex}`;
    const newClaim = {
      days,
      code,
      title,
      subtitle,
      timestamp: new Date().toLocaleString()
    };
    
    setClaimedHardcopies((prev) => {
      const next = { ...prev, [rewardId]: newClaim };
      localStorage.setItem("ebenezer_claimed_hardcopies", JSON.stringify(next));
      return next;
    });
    
    setActiveClaimPass({
      rewardId,
      ...newClaim
    });
  };

  // Recent reads mapping
  const recentReads = useMemo(() => {
    if (!books || books.length === 0) return [];
    return books
      .map((book) => {
        const page = localStorage.getItem(`bookmark_${book.id}`);
        return page ? { ...book, savedPage: parseInt(page, 10) } : null;
      })
      .filter(Boolean);
  }, [books]);

  // Gamification Metrics
  const history = getReadingHistory();
  const streaks = calculateStreak(history);
  const goalProgress = getDailyGoalProgress(history); // dynamically reads matching goal
  const activity = getLast30DaysActivity(history);
  
  const maxPages = useMemo(() => {
    return Math.max(...activity.map((d) => d.count), 5);
  }, [activity]);

  const badges = checkAchievements(history, books);
  const totalPagesRead = activity.reduce((sum, day) => sum + day.count, 0);
  const unlockedCount = badges.filter((b) => b.unlocked).length;

  useSEO({
    title: activeTab === "reading"
      ? "My Library — Ebenezer Books Premium Reading Room"
      : activeTab === "stats"
      ? "Reading consistency & stats — Ebenezer Books Library Dashboard"
      : "My Study Notebook — Ebenezer Books Library Dashboard",
    description: "Your personal Christian reading room. Pick up where you left off, inspect streaks, review daily volume soundwaves, and claim milestones.",
    keywords: "my library, reading stats, habit soundwave, streak rewards, milestones, physical claim",
    ogType: "website",
  });

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)", color: "var(--ink)", transition: "background 0.3s ease, color 0.3s ease" }}>
      {/* Premium Styling Injection */}
      <style>{`
        .library-tabs-container {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 0.35rem;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.09);
          padding: 5px;
          border-radius: 30px;
          width: fit-content;
          min-width: 500px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
        }
        .library-tab-btn {
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.72);
          font-weight: 500;
          font-size: 0.84rem;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: 'Outfit', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          letter-spacing: 0.02em;
        }
        .library-tab-btn:hover {
          color: #FFFFFF;
          background: rgba(255, 255, 255, 0.06);
        }
        .library-tab-btn.active {
          background: var(--gold);
          color: #FFFFFF;
          font-weight: 600;
          box-shadow: 0 4px 14px rgba(200, 146, 42, 0.35);
        }
        .library-tab-btn.active span.tab-icon {
          transform: scale(1.15);
        }
        .tab-icon {
          font-size: 1rem;
          transition: transform 0.25s ease;
          display: inline-block;
        }
        .tab-label-short {
          display: none;
        }
        .tab-label-full {
          display: inline;
        }
        .settings-gear-btn {
          border: none;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.09);
          color: rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          width: 38px;
          height: 38px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
        }
        .settings-gear-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          color: #FFFFFF;
          border-color: rgba(255, 255, 255, 0.2);
        }
        .settings-gear-btn:hover svg {
          transform: rotate(45deg);
        }
        .settings-gear-btn.active {
          background: var(--gold);
          color: #FFFFFF;
          border-color: var(--gold);
          box-shadow: 0 4px 14px rgba(200, 146, 42, 0.35);
        }
        .library-tabs-row {
          width: 100%;
        }
        @media (max-width: 768px) {
          .library-tabs-row {
            flex-wrap: nowrap !important;
          }
          .library-tabs-container {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            flex: 1;
            min-width: 0;
            width: 100%;
            border-radius: 30px;
          }
          .library-tab-btn {
            padding: 8px 4px;
            font-size: 0.76rem;
            gap: 4px;
          }
          .tab-label-full {
            display: none;
          }
          .tab-label-short {
            display: inline;
          }
        }
        @media (max-width: 576px) {
          .library-hero-header {
            padding: 2rem 1rem 1.5rem !important;
          }
        }
      `}</style>
      
      {/* ─── Page Header ─── */}
      <div className="library-hero-header" style={{
        background: "var(--hero-bg)",
        padding: "2.75rem 2rem 2.25rem",
        borderBottom: "1px solid var(--dust)",
        transition: "background 0.3s ease, border-color 0.3s ease",
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          
          {/* Premium Badge */}
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "rgba(200,146,42,0.15)",
            border: "1px solid rgba(200,146,42,0.35)",
            borderRadius: "20px",
            padding: "4px 14px",
            marginBottom: "1.25rem",
          }}>
            <span style={{ fontSize: "0.7rem", color: "var(--gold)" }}>✦</span>
            <span style={{
              fontSize: "0.7rem",
              color: "var(--gold)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
            }}>Reading Room Premium</span>
          </div>

          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "var(--hero-text)",
            fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
            fontWeight: 400,
            marginBottom: "0.5rem",
          }}>
            My <em style={{ color: "var(--gold)", fontStyle: "italic" }}>Library Room</em>
          </h1>
          <p style={{
            color: "var(--hero-muted)",
            fontSize: "0.92rem",
            maxWidth: "500px",
            lineHeight: 1.6,
            margin: 0,
          }}>
            Review your habit accomplishments, inspect daily page soundwaves, pick up active sessions, and claim streak reward passes.
          </p>

          {/* Tab Selector & Settings Outer Row */}
          <div className="library-tabs-row" style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "1.75rem", flexWrap: "wrap", position: "relative" }}>
            {/* Tab Selector */}
            <div className="library-tabs-container">
              {[
                { id: "reading", labelFull: "Reading Progress", labelShort: "Progress", icon: "📖" },
                { id: "notebook", labelFull: "Study Notebook", labelShort: "Notebook", icon: "✍️" },
                { id: "stats", labelFull: "Consistency & Rewards", labelShort: "Rewards", icon: "🏆" },
              ].map((t) => {
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`library-tab-btn ${active ? "active" : ""}`}
                  >
                    <span className="tab-icon">{t.icon}</span>
                    <span className="tab-label-full">{t.labelFull}</span>
                    <span className="tab-label-short">{t.labelShort}</span>
                  </button>
                );
              })}
            </div>

            {/* Settings Gear Button */}
            <button
              onClick={() => setShowGoalSettings(!showGoalSettings)}
              className={`settings-gear-btn ${showGoalSettings ? "active" : ""}`}
              title="Goal Settings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform 0.4s ease" }}>
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>

            {/* Goal settings popover bubble */}
            {showGoalSettings && (
              <div style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: "0.5rem",
                background: "var(--white)",
                border: "1px solid var(--dust)",
                borderRadius: "12px",
                padding: "1rem",
                width: "280px",
                boxShadow: "var(--shadow-lg)",
                zIndex: 100,
                animation: "fadeUp 0.2s ease both",
                color: "var(--ink)",
              }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.05em", fontFamily: "'Outfit', sans-serif" }}>Set Daily Reading Goal</div>
                <div style={{ display: "flex", gap: "6px" }}>
                  {[5, 10, 15, 25, 50].map((val) => {
                    const active = dailyGoal === val;
                    return (
                      <button
                        key={val}
                        onClick={() => {
                          handleGoalChange({ target: { value: String(val) } });
                          setShowGoalSettings(false);
                        }}
                        style={{
                          flex: 1,
                          border: "none",
                          background: active ? "var(--gold)" : "var(--cream)",
                          color: active ? "var(--white)" : "var(--ink)",
                          fontWeight: 600,
                          fontSize: "0.76rem",
                          padding: "6px 0",
                          borderRadius: "16px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          fontFamily: "'Outfit', sans-serif",
                        }}
                      >
                        {val}P
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ─── Main Content Wrapper ─── */}
      <div className="library-content-wrapper" style={{ maxWidth: "1100px", margin: "0 auto", padding: "2.5rem 2rem" }}>

        {/* ─── Cloud Sync Status Banner ─── */}
        {user ? (
          <div style={{
            background: "var(--white)",
            border: "1px solid var(--dust)",
            borderRadius: "16px",
            padding: "1.25rem",
            marginBottom: "1.75rem",
            boxShadow: "var(--shadow)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "1rem",
            animation: "fadeUp 0.4s ease both",
            transition: "background 0.3s, border-color 0.3s"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ fontSize: "1.8rem" }}>☁️</div>
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--ink)", display: "flex", alignItems: "center", gap: "6px" }}>
                  Cloud Synced Profile
                  <span style={{ fontSize: "0.68rem", background: "var(--success-faint)", color: "var(--success)", padding: "2px 8px", borderRadius: "10px", fontWeight: 600 }}>Active</span>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--muted)", marginTop: "2px" }}>
                  Logged in as <strong style={{ color: "var(--ink)" }}>{user.email}</strong>. Streaks and bookmarks are saved securely.
                </div>
              </div>
            </div>
            <button
              onClick={() => syncToCloud()}
              disabled={syncing}
              style={{
                padding: "8px 16px",
                background: "var(--cream)",
                border: "1px solid var(--dust)",
                borderRadius: "20px",
                fontFamily: "'Outfit', sans-serif",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "var(--gold)",
                cursor: syncing ? "wait" : "pointer",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                if (!syncing) {
                  e.currentTarget.style.borderColor = "var(--gold)";
                  e.currentTarget.style.background = "var(--white)";
                }
              }}
              onMouseLeave={(e) => {
                if (!syncing) {
                  e.currentTarget.style.borderColor = "var(--dust)";
                  e.currentTarget.style.background = "var(--cream)";
                }
              }}
            >
              {syncing ? "Syncing..." : "Sync Progress Now"}
            </button>
          </div>
        ) : (
          <div style={{
            background: "linear-gradient(135deg, rgba(200, 146, 42, 0.08) 0%, rgba(200, 146, 42, 0.02) 100%)",
            border: "1px solid rgba(200, 146, 42, 0.25)",
            borderRadius: "16px",
            padding: "1.25rem",
            marginBottom: "1.75rem",
            boxShadow: "var(--shadow)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "1rem",
            animation: "fadeUp 0.4s ease both"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ fontSize: "1.8rem" }}>☁️</div>
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--ink)", display: "flex", alignItems: "center", gap: "6px" }}>
                  Save Your Progress in the Cloud
                  <span style={{ fontSize: "0.68rem", background: "rgba(200, 146, 42, 0.12)", color: "var(--gold)", padding: "2px 8px", borderRadius: "10px", fontWeight: 600 }}>Local Guest Mode</span>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--muted)", marginTop: "2px" }}>
                  Create an account to backup streaks, reading history, and bookmarks permanently.
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/login")}
              style={{
                padding: "8px 20px",
                background: "var(--gold)",
                color: "white",
                border: "none",
                borderRadius: "20px",
                fontFamily: "'Outfit', sans-serif",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.25s",
                boxShadow: "0 3px 8px rgba(200, 146, 42, 0.2)"
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
            >
              Sign In & Sync
            </button>
          </div>
        )}

        {/* ─────────────────────────────────────────────────────────────────────── */}
        {/* TAB 1: READING PROGRESS                                                 */}
        {/* ─────────────────────────────────────────────────────────────────────── */}
        {activeTab === "reading" && (
          <>
            {recentReads.length > 0 && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "1.75rem",
              }}>
                <h2 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  color: "var(--ink)",
                  margin: 0,
                }}>Continue Reading</h2>
                <div style={{
                  height: "1px",
                  flex: 1,
                  background: "linear-gradient(90deg, var(--dust), transparent)",
                }} />
                <span style={{
                  fontSize: "0.78rem",
                  color: "var(--gold)",
                  fontWeight: 600,
                  fontFamily: "'Outfit', sans-serif",
                }}>{recentReads.length} book{recentReads.length !== 1 ? "s" : ""} in progress</span>
              </div>
            )}

            {recentReads.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "1.25rem",
                animation: "fadeUp 0.4s ease both",
              }}>
                {recentReads.map((book) => {
                  const totalPages = book.pages || 100;
                  const percent = Math.min(Math.round((book.savedPage / totalPages) * 100), 100);
                  const isComplete = percent >= 100;

                  return (
                    <div
                      key={book.id}
                      onClick={() => navigate(`/book/${book.id}/read`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && navigate(`/book/${book.id}/read`)}
                      style={{
                        background: "var(--white)",
                        border: "1px solid var(--dust)",
                        borderRadius: "16px",
                        padding: "1.25rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        cursor: "pointer",
                        boxShadow: "var(--shadow)",
                        transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, background 0.3s ease",
                        position: "relative",
                        overflow: "hidden",
                        outline: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "var(--shadow-lg)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "none";
                        e.currentTarget.style.boxShadow = "var(--shadow)";
                      }}
                    >
                      {isComplete && (
                        <div style={{
                          position: "absolute",
                          top: "12px",
                          right: "-22px",
                          background: "var(--success)",
                          color: "#fff",
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          padding: "3px 30px",
                          transform: "rotate(35deg)",
                          fontFamily: "'Outfit', sans-serif",
                        }}>DONE</div>
                      )}

                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div style={{
                          width: "52px",
                          height: "70px",
                          background: `linear-gradient(160deg, ${book.coverColor?.[0] || book.colorFrom || "#1a1208"}, ${book.coverColor?.[1] || book.colorTo || "#C8922A"})`,
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                        }}>
                          <span style={{ fontSize: "1.4rem" }}>📖</span>
                          <div style={{
                            position: "absolute", top: 0, right: "7px",
                            width: "7px", height: "14px",
                            background: "var(--gold)", borderRadius: "0 0 3px 3px",
                          }} />
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "var(--ink)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontFamily: "'Cormorant Garamond', serif",
                          }}>{book.title}</div>
                          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "2px" }}>
                            by {book.author}
                          </div>
                        </div>

                        <div style={{
                          width: "36px", height: "36px",
                          borderRadius: "50%",
                          background: "var(--cream)",
                          color: "var(--gold)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          flexShrink: 0,
                        }}>▶</div>
                      </div>

                      <div style={{ marginTop: "0.1rem" }}>
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.72rem",
                          color: "var(--muted)",
                          marginBottom: "6px",
                          fontWeight: 500,
                        }}>
                          <span>Page {book.savedPage} of {totalPages}</span>
                          <span style={{ color: isComplete ? "var(--success)" : "var(--gold)", fontWeight: 700 }}>
                            {isComplete ? "✓ Finished" : `${percent}% read`}
                          </span>
                        </div>
                        <div style={{
                          width: "100%", height: "6px",
                          background: "var(--dust)",
                          borderRadius: "3px",
                          overflow: "hidden",
                        }}>
                          <div style={{
                            width: `${percent}%`,
                            height: "100%",
                            background: isComplete
                              ? "linear-gradient(90deg, var(--success), var(--success-2))"
                              : "linear-gradient(90deg, var(--gold), var(--gold-light))",
                            borderRadius: "3px",
                            transition: "width 0.5s ease",
                          }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: "center",
                padding: "6rem 2rem",
                animation: "fadeUp 0.4s ease both",
              }}>
                <div style={{
                  width: "80px", height: "80px",
                  background: "linear-gradient(135deg, #1A1208, #3D2A12)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2rem",
                  margin: "0 auto 1.5rem",
                  boxShadow: "0 8px 24px rgba(26,18,8,0.15)",
                }}>📖</div>

                <h2 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.8rem",
                  color: "var(--ink)",
                  fontWeight: 400,
                  marginBottom: "0.75rem",
                }}>Your Reading Room is Empty</h2>

                <p style={{
                  fontSize: "0.95rem",
                  color: "var(--muted)",
                  maxWidth: "420px",
                  margin: "0 auto 2rem",
                  lineHeight: 1.7,
                  fontFamily: "'Outfit', sans-serif",
                }}>
                  Open any book from the catalog and click <strong style={{ color: "var(--ink)" }}>"Read Online"</strong> to start reading. Your progress will appear here automatically.
                </p>

                <button
                  onClick={() => navigate("/")}
                  style={{
                    background: "var(--ink)",
                    color: "var(--cream)",
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 28px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 0.2s, color 0.2s, transform 0.15s",
                    fontFamily: "'Outfit', sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--gold)";
                    e.currentTarget.style.color = "var(--cream)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--ink)";
                    e.currentTarget.style.color = "var(--cream)";
                  }}
                >
                  Browse the Library
                </button>
              </div>
            )}
          </>
        )}

        {/* ─────────────────────────────────────────────────────────────────────── */}
        {/* TAB 2: CONSISTENCY & REWARDS                                            */}
        {/* ─────────────────────────────────────────────────────────────────────── */}
        {activeTab === "stats" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "1.75rem",
            }}>
              <h2 style={{
                fontFamily: "var(--font-display)",
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "var(--ink)",
                margin: 0,
              }}>Consistency & Rewards</h2>
              <div style={{
                height: "1px",
                flex: 1,
                background: "linear-gradient(90deg, var(--dust), transparent)",
              }} />
            </div>

            {/* ─── Stats Overview Row ─── */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.25rem",
              marginBottom: "1.5rem",
            }}>
              
              <StreakCard streaks={streaks} />

              <GoalProgressCard goalProgress={goalProgress} />

              {/* TOTAL PAGES & BADGES SUMMARY CARD */}
              <div style={{
                background: "var(--white)",
                border: "1px solid var(--dust)",
                borderRadius: "16px",
                padding: "1.5rem",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                alignItems: "center",
                gap: "1rem",
                boxShadow: "var(--shadow)",
                transition: "background 0.3s ease, border-color 0.3s ease",
              }}>
                <div style={{ borderRight: "1px solid var(--dust)", paddingRight: "0.5rem" }}>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: "0.72rem",
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}>
                    Total Pages (30d)
                  </div>
                  <h3 style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "2.1rem",
                    fontWeight: 700,
                    color: "var(--gold)",
                    margin: "4px 0 0",
                  }}>{totalPagesRead}</h3>
                </div>
                <div style={{ paddingLeft: "0.5rem" }}>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: "0.72rem",
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}>
                    Unlocked Badges
                  </div>
                  <h3 style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "2.1rem",
                    fontWeight: 700,
                    color: "var(--ink)",
                    margin: "4px 0 0",
                  }}>
                    {unlockedCount} <span style={{ fontSize: "1rem", color: "var(--muted)", fontWeight: 400 }}>/ {badges.length}</span>
                  </h3>
                </div>
              </div>

            </div>

            {/* ─── Habits & Streak Rewards Hub (Grid Container) ─── */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.5rem",
              alignItems: "stretch",
              marginTop: "1.5rem",
              marginBottom: "1.5rem",
            }}>
              
              <ActivitySoundwave
                activity={activity}
                maxPages={maxPages}
                dailyGoal={dailyGoal}
                streaks={streaks}
              />

              <HardcopyRewardsShelf
                streaks={streaks}
                claimedHardcopies={claimedHardcopies}
                claimHardcopy={claimHardcopy}
                setActiveClaimPass={setActiveClaimPass}
              />

            </div>

            <BadgesGallery badges={badges} />

          </div>
        )}

        {activeTab === "notebook" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <StudyNotebook books={books} />
          </div>
        )}

        {/* ─── Premium Hardcopy Claims Ticket Pass Modal ─── */}
        <ClaimPassModal
          activeClaimPass={activeClaimPass}
          setActiveClaimPass={setActiveClaimPass}
        />
      </div>

      {/* Embedded Animations and Shimmers */}
      <style>{`
        @keyframes scaleUp {
          from { transform: scale(0.9) translateY(12px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes pulseGold {
          0%, 100% { box-shadow: 0 0 15px rgba(200, 146, 42, 0.25); transform: scale(1); }
          50% { box-shadow: 0 0 30px rgba(200, 146, 42, 0.55); transform: scale(1.04); }
        }
        @keyframes bounceMini {
          from { transform: translateX(-50%) translateY(0); }
          to { transform: translateX(-50%) translateY(-4px); }
        }
        @keyframes flicker {
          0%, 100% { transform: scale(1) rotate(-1deg); opacity: 0.95; }
          20% { transform: scale(1.06) rotate(1deg); opacity: 1; }
          40% { transform: scale(0.96) rotate(-0.5deg); opacity: 0.88; }
          60% { transform: scale(1.03) rotate(0.5deg); opacity: 1; }
          80% { transform: scale(0.98) rotate(0deg); opacity: 0.92; }
        }
        @media (max-width: 576px) {
          .library-content-wrapper {
            padding: 1.5rem 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}
